/**
 * useSourceChapterFix — « Chapitre non fiable » devient la correction du chapitre, pour les trois surfaces de
 * « Générer... » (l'ancienne fenêtre `ParcoursChoiceModal.js`, « Générer à partir de l'exo », le fil de la
 * salle) : la même règle (`sourceChapterFix`), la même liste, le même appel.
 *
 *  - la liste : `getChaptersAsTutor` de l'élève, la matière du support et la classe de l'élève — celle de
 *    « Générer une activité ». Appel direct, comme le catalogue des matières du parcours partagé : la réponse
 *    n'a qu'un lecteur, et la tranche partagée servirait aussi le formulaire ouvert ailleurs ;
 *  - le choix d'un chapitre part par la correction (`PATCH materials/{id}/chapter`) ; sa réponse est écrite au
 *    store (`chapterConfidenceById`), que ce crochet relit : la génération se débloque aussitôt, et le reste à
 *    la réouverture ;
 *  - sans aucun chapitre pour la matière dans la classe, la même correction sans chapitre, puis la génération.
 *
 * La correction se montre tant que la surface est ouverte sur ce support (`shown`), même une fois enregistrée :
 * le chapitre choisi reste lisible et se change encore.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { chaptersService } from "../../../../data/services/chaptersService"
import { materialsService } from "../../../../data/services/materialsService"
import {
    toUISectionedChapters,
    type ApiCatalogChapter,
} from "../../adapters/parent/SchoolAssessments/sectionedChapterAdapter"
import { type UISectionedChapter } from "../types"
import {
    SOURCE_CHAPTER_FIX_TEXTS,
    sourceChapterFixBody,
    sourceChapterFixStatus,
    sourceNeedsChapter,
    type SourceChapterFixStatus,
} from "../../view-models/tutor/Students/sourceChapterFix"
import { useApiMutation } from "./useApiMutation"

type Id = string | number

export type SourceChapterFixSource = {
    materialId: Id
    /** `material.structured_data.analysis.confidence`, telle que l'écran la tient. */
    confidence: string | null
    /** `material.school_subject_id` : la matière dont on liste les chapitres. */
    subjectId: Id | null
}

export type UseSourceChapterFixArgs = {
    source: SourceChapterFixSource | null
    /** L'élève dont on lit les chapitres (`/students/{id}/chapters`). */
    studentId: Id | null
    /** La classe de l'élève (à défaut, celle du support). */
    className: string | null
    /** La surface est ouverte : rien ne se lit avant. */
    active: boolean
}

export type SourceChapterFix = {
    /** Le support est à corriger, correction du masteur lue. */
    unreliable: boolean
    /** La correction se montre : le support était à corriger à l'ouverture de la surface. */
    shown: boolean
    status: SourceChapterFixStatus
    chapters: ApiCatalogChapter[]
    /** La même liste, telle que le champ Chapitre du design system la lit. */
    sectionedChapters: UISectionedChapter[]
    /** Le chapitre que le masteur a choisi ici, une fois enregistré. */
    chapterId: Id | null
    saving: boolean
    error: string | null
    /** Enregistre le chapitre choisi — rien quand c'est celui déjà enregistré, ou déjà en route. */
    pick: (chapterId: Id) => void
    /** Aucun chapitre à choisir : le support est confirmé sans chapitre, puis `then` (la génération) part. */
    confirmWithoutChapter: (then: () => void) => void
    retry: () => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type State = any

/** Aucune liste : une identité stable, que les `useMemo` des surfaces ne voient pas changer à chaque rendu. */
const NO_CHAPTERS: ApiCatalogChapter[] = []

export function useSourceChapterFix({
    source,
    studentId,
    className,
    active,
}: UseSourceChapterFixArgs): SourceChapterFix {
    const materialId = source?.materialId ?? null
    const unreliable = useSelector((s: State) =>
        source != null
            ? sourceNeedsChapter(materialId, source.confidence, s?.data?.materials?.chapterConfidenceById)
            : false
    )

    // La surface s'ouvre sur un support à corriger : la correction se montre jusqu'à sa fermeture.
    const [shownFor, setShownFor] = useState<string | null>(null)
    useEffect(() => {
        if (!active || materialId == null) {
            setShownFor(null)
            return
        }
        if (unreliable) setShownFor(String(materialId))
    }, [active, materialId, unreliable])
    const shown = active && materialId != null && shownFor === String(materialId)

    // ── La liste des chapitres de la matière du support, dans la classe de l'élève.
    const subjectId = source?.subjectId ?? null
    const [chapters, setChapters] = useState<ApiCatalogChapter[] | null>(null)
    const [failed, setFailed] = useState(false)
    const [attempt, setAttempt] = useState(0)
    useEffect(() => {
        setChapters(null)
        setFailed(false)
        if (!shown) return
        if (studentId == null) {
            setChapters([])
            return
        }
        // Une matière ou une classe inconnue élargit la liste (toutes les matières de la classe, toutes les classes
        // de la matière) plutôt que de la vider : la correction sans chapitre ne vaut que quand il n'y a vraiment
        // rien à choisir, et le serveur l'élargit de même (`Chapter::programmeExists`).
        let cancelled = false
        chaptersService
            .getChaptersAsTutor(
                { id: studentId },
                {},
                {
                    ...(subjectId != null && { school_subject_id: subjectId }),
                    ...(className != null && { class: className }),
                    page_size: 100,
                }
            )
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((response: any) => {
                if (cancelled) return
                const payload = response?.data?.data ?? response?.data
                setChapters(Array.isArray(payload?.chapters) ? payload.chapters : [])
            })
            .catch(() => {
                if (!cancelled) setFailed(true)
            })
        return () => {
            cancelled = true
        }
    }, [shown, subjectId, className, studentId, attempt])

    // ── La correction : le chapitre choisi, ou aucun.
    const [error, setError] = useState<string | null>(null)
    const [chapterId, setChapterId] = useState<Id | null>(null)
    useEffect(() => {
        setError(null)
        setChapterId(null)
    }, [materialId, shown])
    const thenRef = useRef<(() => void) | null>(null)
    const pendingChapterRef = useRef<Id | null>(null)
    const awaitingRef = useRef(false)
    const savedRef = useRef<Id | null>(null)
    savedRef.current = chapterId
    const { start, requesting } = useApiMutation({
        service: materialsService.updateMaterialChapter,
        scopeParam: "id",
        fallback: SOURCE_CHAPTER_FIX_TEXTS.saveFailed,
        surface: "generate_from_activity",
        onSuccess: () => {
            awaitingRef.current = false
            setChapterId(pendingChapterRef.current)
            const then = thenRef.current
            thenRef.current = null
            then?.()
        },
        onError: (message) => {
            awaitingRef.current = false
            thenRef.current = null
            setError(message || SOURCE_CHAPTER_FIX_TEXTS.saveFailed)
        },
    })
    const send = useCallback(
        (chosen: Id | null) => {
            if (materialId == null) return
            setError(null)
            pendingChapterRef.current = chosen
            awaitingRef.current = true
            start({ id: materialId }, sourceChapterFixBody(chosen, className))
        },
        [materialId, className, start]
    )
    const pick = useCallback(
        (chosen: Id) => {
            const same = (other: Id | null) => other != null && String(other) === String(chosen)
            if (same(savedRef.current) || (awaitingRef.current && same(pendingChapterRef.current))) return
            thenRef.current = null
            send(chosen)
        },
        [send]
    )
    const confirmWithoutChapter = useCallback(
        (then: () => void) => {
            thenRef.current = then
            send(null)
        },
        [send]
    )

    const list = chapters ?? NO_CHAPTERS
    const sectionedChapters = useMemo(() => toUISectionedChapters(list), [list])

    return {
        unreliable,
        shown,
        status: sourceChapterFixStatus(unreliable, chapters?.length ?? null, failed),
        chapters: list,
        sectionedChapters,
        chapterId,
        saving: requesting,
        error,
        pick,
        confirmWithoutChapter,
        retry: () => setAttempt((value) => value + 1),
    }
}
